import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Heart, Users, Shield, Star, Briefcase, Leaf, Brain, Zap, Target, MessageCircle, CheckCircle, TrendingUp, Calendar, Eye, BadgeCheck, Wallet } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const howItWorks = [
  {
    step: '01',
    title: 'Conte sobre você',
    description: 'Responda algumas perguntas simples sobre o que busca e como se sente.',
    icon: MessageCircle
  },
  {
    step: '02',
    title: 'IA analisa seu perfil',
    description: 'Nosso algoritmo cruza suas necessidades com centenas de profissionais verificados.',
    icon: Brain
  },
  {
    step: '03',
    title: 'Receba seu match',
    description: 'Receba recomendações personalizadas com os profissionais mais compatíveis.',
    icon: Target
  },
  {
    step: '04',
    title: 'Comece sua jornada',
    description: 'Agende sua primeira sessão por chat, vídeo, ligação ou email.',
    icon: Zap
  }
];

const professionalTypes = [
  {
    type: 'therapist',
    title: 'Terapeutas',
    description: 'Ansiedade, depressão, autoestima e mais',
    icon: Heart,
    color: 'bg-[#6B8E7C]'
  },
  {
    type: 'coach',
    title: 'Mentores',
    description: 'Carreira, liderança, produtividade',
    icon: Briefcase,
    color: 'bg-[#D4A373]'
  },
  {
    type: 'spiritualist',
    title: 'Espiritualistas',
    description: 'Tarot, reiki, meditação e mais',
    icon: Sparkles,
    color: 'bg-[#9B8BAE]'
  }
];

export default function HomePage() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section - AI First */}
        <section className="relative py-24 lg:py-36 overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 -z-10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#F9F8F6] via-[#F6EBE6]/40 to-[#F9F8F6]" />
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#4A5D4E]/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#D4A373]/5 rounded-full blur-3xl" />
          </div>

          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#4A5D4E]/10 text-[#4A5D4E] text-sm font-medium mb-8 animate-fade-in">
                <Brain className="w-4 h-4" strokeWidth={1.5} />
                Matching inteligente com IA
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light tracking-tighter leading-[1.1] text-[#292927] animate-fade-in-delay-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                A IA encontra o<br />
                <span className="text-[#4A5D4E]">profissional ideal</span><br />
                para você
              </h1>
              
              <p className="mt-8 text-lg text-[#63635E] max-w-2xl mx-auto animate-fade-in-delay-2">
                Conectamos você com psicólogos, terapeutas, e espiritualistas através de um algoritmo inteligente que entende as suas necessidades.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-delay-3">
                <Link
                  to="/match"
                  className="btn-primary w-full sm:w-auto text-base py-4 px-10 flex items-center justify-center gap-3"
                  data-testid="hero-cta-match"
                >
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                  Encontrar meu Match Agora
                </Link>
              </div>

              <p className="mt-4 text-sm text-[#A0A09A] animate-fade-in-delay-3">
                Gratuito, rápido e sem compromisso
              </p>
            </div>
          </div>
        </section>

        {/* How AI Match Works */}
        <section className="py-20 lg:py-28 bg-white" data-testid="how-it-works-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F6EBE6] text-[#4A5D4E] text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Simples e rápido
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Como o Match IA funciona
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((item, idx) => (
                <div 
                  key={item.step}
                  className="relative text-center p-8 rounded-3xl bg-[#F9F8F6] animate-fade-in group hover:bg-[#4A5D4E] transition-colors duration-300"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                  data-testid={`step-${item.step}`}
                >
                  <div className="text-5xl font-light text-[#EAE8E3] group-hover:text-white/10 mb-4 transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {item.step}
                  </div>
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-[#4A5D4E]/10 group-hover:bg-white/20 flex items-center justify-center mb-5 transition-colors">
                    <item.icon className="w-7 h-7 text-[#4A5D4E] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-[#292927] group-hover:text-white mb-3 transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#63635E] group-hover:text-white/80 transition-colors">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/match"
                className="btn-primary inline-flex items-center gap-2 text-base py-3.5 px-8"
                data-testid="how-it-works-cta"
              >
                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                Começar meu Match
              </Link>
            </div>
          </div>
        </section>

        {/* Professional Types - as tags/categories for AI */}
        <section className="py-20 lg:py-28" data-testid="professional-types-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Especialistas em diversas áreas
              </h2>
              <p className="mt-4 text-[#63635E]">
                Nossa IA encontra o profissional certo entre todas as especialidades
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {professionalTypes.map((item, idx) => (
                <Link
                  key={item.type}
                  to="/match"
                  className="p-6 rounded-2xl border-2 border-[#EAE8E3] hover:border-[#4A5D4E] bg-white hover:bg-[#4A5D4E]/5 transition-all text-center group animate-fade-in"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  data-testid={`type-card-${item.type}`}
                >
                  <div className={`w-14 h-14 mx-auto rounded-2xl ${item.color} flex items-center justify-center mb-4`}>
                    <item.icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-[#292927] mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {item.title}
                  </h3>
                  <p className="text-xs text-[#63635E]">
                    {item.description}
                  </p>
                  <div className="mt-3 text-xs text-[#4A5D4E] font-medium flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Sparkles className="w-3 h-3" />
                    Encontrar match
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Stats / Social Proof */}
        <section className="py-16 bg-[#4A5D4E]" data-testid="stats-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '500+', label: 'Profissionais verificados' },
                { value: '98%', label: 'Taxa de satisfação' },
                { value: '10k+', label: 'Matches realizados' },
                { value: '4.8', label: 'Avaliação média' }
              ].map((stat, idx) => (
                <div key={stat.label} className="animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                  <p className="text-3xl sm:text-4xl font-light text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stat.value}
                  </p>
                  <p className="text-sm text-white/70">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 lg:py-28" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Quem usou, recomenda
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  name: 'Mariana L.',
                  role: 'Match encontrou sua terapeuta',
                  text: 'Respondi o questionario e em segundos recebi 3 recomendacoes perfeitas. Escolhi a Dra. Ana e ja sao 6 meses de terapia transformadora.',
                  rating: 5
                },
                {
                  name: 'Ricardo S.',
                  role: 'Match encontrou seu mentor',
                  text: 'A IA entendeu exatamente o que eu precisava. O mentor que me recomendou me ajudou a traçar um plano de carreira claro. Incrível!',
                  rating: 5
                },
                {
                  name: 'Julia M.',
                  role: 'Match encontrou sua espiritualista',
                  text: 'Nunca pensei que um algoritmo poderia entender algo tão pessoal. O match foi perfeito, a Luna é uma profissional excepcional.',
                  rating: 5
                }
              ].map((testimonial, idx) => (
                <div 
                  key={testimonial.name}
                  className="card-theraflow p-8 animate-fade-in"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${i}`} className="w-4 h-4 text-[#D4A373] fill-[#D4A373]" strokeWidth={1.5} />
                    ))}
                  </div>
                  <p className="text-[#63635E] text-sm leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>
                  <div>
                    <p className="font-medium text-[#292927]">{testimonial.name}</p>
                    <p className="text-xs text-[#4A5D4E] font-medium">{testimonial.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECTION: Para Profissionais ===== */}
        <section className="py-20 lg:py-28 bg-white relative overflow-hidden" data-testid="professionals-section">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#4A5D4E]/3 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#D4A373]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#D4A373]/10 text-[#D4A373] text-sm font-medium mb-4">
                <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                Para Profissionais
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-normal tracking-tight text-[#292927]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Expanda sua prática com a Betherapy
              </h2>
              <p className="mt-4 text-[#63635E] max-w-2xl mx-auto">
                Junte-se a centenas de profissionais que já ampliam seu alcance e atendem mais clientes através da nossa plataforma.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
              {[
                {
                  icon: Eye,
                  title: 'Visibilidade ampliada',
                  description: 'Seu perfil é encontrado por milhares de pessoas buscando ajuda profissional. A IA conecta você aos clientes mais compatíveis.',
                  color: 'bg-[#6B8E7C]'
                },
                {
                  icon: Calendar,
                  title: 'Agenda organizada',
                  description: 'Gerencie sessões por chat, vídeo, ligação ou email. Tudo integrado com notificações automáticas.',
                  color: 'bg-[#4A5D4E]'
                },
                {
                  icon: Wallet,
                  title: 'Pagamentos garantidos',
                  description: 'Receba via Pix, cartão ou PayPal. Pagamento é obrigatório antes da sessão — sem calotes.',
                  color: 'bg-[#D4A373]'
                },
                {
                  icon: TrendingUp,
                  title: 'Cresça sua carreira',
                  description: 'Dashboard com métricas de desempenho, avaliações de clientes e histórico de ganhos.',
                  color: 'bg-[#9B8BAE]'
                },
                {
                  icon: BadgeCheck,
                  title: 'Selo de verificação',
                  description: 'Profissionais verificados recebem destaque na busca e mais confiança dos clientes.',
                  color: 'bg-[#6B8E7C]'
                },
                {
                  icon: Shield,
                  title: 'Cadastro 100% gratuito',
                  description: 'Crie seu perfil, defina seus preços e comece a atender sem pagar nada. Sem taxas de adesão.',
                  color: 'bg-[#4A5D4E]'
                }
              ].map((benefit, idx) => (
                <div
                  key={benefit.title}
                  className="p-6 rounded-2xl border border-[#EAE8E3] bg-[#F9F8F6]/50 hover:border-[#4A5D4E]/30 hover:shadow-sm transition-all animate-fade-in"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <div className={`w-12 h-12 rounded-xl ${benefit.color} flex items-center justify-center mb-4`}>
                    <benefit.icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-base font-medium text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-[#63635E] leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>

            {/* How it works for professionals */}
            <div className="rounded-3xl bg-[#292927] p-8 sm:p-12 mb-16">
              <h3 className="text-xl sm:text-2xl font-normal text-white text-center mb-10" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Como funciona para o profissional
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { step: '01', title: 'Cadastre-se grátis', desc: 'Crie sua conta e preencha seu perfil profissional com suas especialidades.' },
                  { step: '02', title: 'Defina seus preços', desc: 'Configure valores por modalidade: chat, vídeo, ligação ou email.' },
                  { step: '03', title: 'Receba clientes', desc: 'Nossa IA direciona clientes compatíveis diretamente ao seu perfil.' },
                  { step: '04', title: 'Atenda e receba', desc: 'Realize sessões pela plataforma e receba seus pagamentos em dia.' }
                ].map((item, idx) => (
                  <div key={item.step} className="text-center animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                    <div className="text-4xl font-light text-[#D4A373] mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      {item.step}
                    </div>
                    <h4 className="text-base font-medium text-white mb-2">{item.title}</h4>
                    <p className="text-sm text-white/60">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Testimonials */}
            <div className="mb-16">
              <h3 className="text-xl sm:text-2xl font-normal text-[#292927] text-center mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                O que dizem nossos profissionais
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    name: 'Dra. Ana Silva',
                    type: 'Terapeuta',
                    text: 'Desde que entrei na Betherapy, minha agenda ficou muito mais organizada. Os clientes chegam já compatíveis com minha abordagem, o que facilita muito o trabalho.',
                    rating: 5
                  },
                  {
                    name: 'Carlos Mendes',
                    type: 'Mentor de Carreira',
                    text: 'A plataforma me deu visibilidade que eu não conseguiria sozinho. Em 3 meses, dobrei meu número de atendimentos. E o melhor: cadastro gratuito!',
                    rating: 5
                  },
                  {
                    name: 'Luna Estrela',
                    type: 'Espiritualista',
                    text: 'Receber pagamento antes da sessão mudou tudo. Não tenho mais problemas com cancelamentos de última hora. A plataforma é muito profissional.',
                    rating: 5
                  }
                ].map((t, idx) => (
                  <div
                    key={t.name}
                    className="p-6 rounded-2xl border border-[#EAE8E3] bg-white animate-fade-in"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(t.rating)].map((_, i) => (
                        <Star key={`ps-${i}`} className="w-3.5 h-3.5 text-[#D4A373] fill-[#D4A373]" strokeWidth={1.5} />
                      ))}
                    </div>
                    <p className="text-sm text-[#63635E] leading-relaxed mb-4">"{t.text}"</p>
                    <div>
                      <p className="text-sm font-medium text-[#292927]">{t.name}</p>
                      <p className="text-xs text-[#4A5D4E]">{t.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="text-center rounded-3xl bg-gradient-to-br from-[#4A5D4E] to-[#3A4A3E] p-10 sm:p-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-white text-sm font-medium mb-6">
                <CheckCircle className="w-4 h-4" strokeWidth={1.5} />
                Cadastro 100% gratuito
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Comece a atender hoje mesmo
              </h3>
              <p className="text-white/70 max-w-xl mx-auto mb-8">
                Cadastre-se gratuitamente, monte seu perfil e deixe a IA conectar você aos clientes certos. Sem mensalidade, sem taxa de adesão.
              </p>
              <Link
                to="/register/professional"
                className="inline-flex items-center gap-3 bg-white text-[#4A5D4E] font-medium py-4 px-10 rounded-full hover:bg-[#F6EBE6] transition-colors text-base"
                data-testid="pro-cta-register"
              >
                <Briefcase className="w-5 h-5" strokeWidth={1.5} />
                Cadastre-se como Profissional
                <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-sm text-white/50">
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Gratuito para sempre</span>
                <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> Cadastro em 5 min</span>
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> +500 profissionais</span>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 lg:py-28 bg-white" data-testid="cta-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="ai-matching-card text-center py-16">
              <div className="absolute inset-0 animate-shimmer opacity-50" />
              <div className="relative z-10">
                <Brain className="w-14 h-14 mx-auto text-[#4A5D4E] mb-6" strokeWidth={1} />
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Pronto para encontrar<br />seu profissional ideal?
                </h2>
                <p className="text-lg text-[#63635E] max-w-2xl mx-auto mb-8">
                  Leva menos de 2 minutos. Nossa IA faz o resto.
                </p>
                <Link
                  to="/match"
                  className="btn-primary inline-flex items-center gap-3 text-base py-4 px-10"
                  data-testid="cta-start-match"
                >
                  <Sparkles className="w-5 h-5" strokeWidth={1.5} />
                  Começar Match Gratuito
                </Link>
                <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#A0A09A]">
                  <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Gratuito</span>
                  <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" /> 2 min</span>
                  <span className="flex items-center gap-1.5"><Heart className="w-4 h-4" /> Sem compromisso</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
